import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  Clock,
  ListMusic,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import type { BackendTask } from '@dt-pm/core';
import { env } from '@/app/config/env';
import { Field, Textarea, useToast } from '@/design-system';
import { TimerToggleButton } from '@/shared/components/TimerToggleButton';
import { useTaskTimer } from '@/features/projects/lib/work-item-timer';
import { api } from '@/shared/lib/api';
import { cn } from '@/shared/lib/cn';
import {
  formatTimerHMS,
  placeBesideClock,
  TIMER_PANEL_OPEN_EVENT,
  type TimerPanelOpenDetail,
  resolveTimerCardKind,
  type TimerOption,
} from './timer-panel-state';

interface TimerTaskNode extends TimerOption {
  subtasks: TimerOption[];
}

/** Busca de projetos: debounce de 300ms para não sobrecarregar o backend. */
const PROJECT_SEARCH_DEBOUNCE_MS = 300;

function buildTaskTree(items: BackendTask[]): TimerTaskNode[] {
  // Tasks não têm hierarquia (parentId) - cada task é um nó raiz
  return items.map((item) => ({
    id: item.id,
    primary: item.name,
    secondary: item.code || undefined,
    subtasks: [], // Tasks não têm subtasks hierárquicos
  }));
}

function flattenTaskOptions(tasks: readonly TimerTaskNode[]): TimerOption[] {
  const options: TimerOption[] = [];
  for (const task of tasks) {
    options.push({ id: task.id, primary: task.primary, secondary: task.secondary });
    for (const subtask of task.subtasks) {
      options.push({
        id: subtask.id,
        primary: `${task.primary} / ${subtask.primary}`,
        secondary: subtask.secondary,
      });
    }
  }
  return options;
}

const PANEL =
  'w-[320px] rounded-xl border border-line bg-surface p-6 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]';

const ICON_BUTTON =
  'flex size-10 shrink-0 items-center justify-center rounded-xl text-ink-muted hover:bg-canvas cursor-pointer';

/**
 * Cronômetro da aplicação — US-14. O recolhimento pertence exclusivamente à extensão do navegador.
 */
export function SidebarTimer() {
  const { show } = useToast();
  const timer = useTaskTimer();
  const clockRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 84 });
  const [panelAnchorRect, setPanelAnchorRect] = useState<TimerPanelOpenDetail['anchorRect'] | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [projectSearch, setProjectSearch] = useState('');
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [projects, setProjects] = useState<TimerOption[]>([]);
  const [tasks, setTasks] = useState<TimerTaskNode[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const displayProjectId = timer.runningProjectId ?? projectId;
  const displayTaskId = timer.runningTaskId ?? taskId;
  const isRunning = Boolean(timer.runningTaskId);
  const seconds = timer.secondsFor(displayTaskId ?? '');

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === displayProjectId) ?? null,
    [projects, displayProjectId],
  );
  const taskOptions = useMemo(() => flattenTaskOptions(tasks), [tasks]);
  const selectedTask = useMemo(
    () => taskOptions.find((task) => task.id === displayTaskId) ?? null,
    [taskOptions, displayTaskId],
  );

  /** Busca projetos com debounce — uma chamada por vez, cancela a anterior se necessário. */
  const loadProjectsDebounced = useCallback(
    async (searchTerm: string) => {
      setProjectSearchLoading(true);
      try {
        const [result, customers] = await Promise.all([
          api.projects.list({ tab: 'ACTIVE', limit: 50, search: searchTerm || undefined }),
          api.customers.list().catch(() => []),
        ]);
        const customerName = new Map(customers.map((customer) => [customer.id, customer.name]));
        setProjects(
          result.items
            .map((item) => ({ id: item.id, primary: item.name, secondary: customerName.get(item.customerId) }))
            .sort((a, b) => a.primary.localeCompare(b.primary, 'pt-BR')),
        );
      } catch {
        setProjects([]);
      } finally {
        setProjectSearchLoading(false);
      }
    },
    [],
  );

  // Debounce da busca de projetos — não recarrega se já tiver projetos e search vazio (seleção não deve recarregar)
  useEffect(() => {
    if (!isOpen) return;
    // Se search está vazio e já temos projetos, não recarrega (evita recarregar ao selecionar)
    if (!projectSearch && projects.length > 0) return;
    const timerId = setTimeout(() => {
      void loadProjectsDebounced(projectSearch);
    }, PROJECT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timerId);
  }, [projectSearch, isOpen, loadProjectsDebounced, projects.length]);

  const loadTasks = useCallback(async (pid: string) => {
    setTasksLoading(true);
    try {
      // Tasks são sempre time-trackable na nova estrutura
      const items = await api.tasks.list(pid);
      setTasks(buildTaskTree(items));
    } catch {
      setTasks([]);
      show({ title: 'Não foi possível carregar as tarefas do projeto.', tone: 'error' });
    } finally {
      setTasksLoading(false);
    }
  }, [show]);


  const preparePanelData = useCallback(() => {
    // Carrega apenas a primeira página de projetos (busca incremental substitui o carregamento completo)
    void loadProjectsDebounced('');
    const pid = timer.runningProjectId ?? projectId;
    if (pid) void loadTasks(pid);
  }, [loadProjectsDebounced, loadTasks, projectId, timer.runningProjectId]);

  function handleOpen(): void {
    const next = !isOpen;
    setPanelAnchorRect(null);
    setIsOpen(next);
    if (next) {
      timer.setMiniPlayerVisible(false);
      preparePanelData();
    }
  }

  useLayoutEffect(() => {
    if (!isOpen) return;

    function updatePosition(): void {
      const clock = panelAnchorRect ?? clockRef.current?.getBoundingClientRect();
      const panel = panelRef.current;
      if (!clock || !panel) return;
      const next = placeBesideClock(
        clock,
        { width: panel.offsetWidth, height: panel.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      );
      setPanelPos((current) =>
        current.top === next.top && current.left === next.left ? current : next,
      );
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, panelAnchorRect]);

  useEffect(() => {
    function handleTimerPanelOpen(event: Event) {
      const detail = (event as CustomEvent<TimerPanelOpenDetail>).detail;
      setPanelAnchorRect(detail?.anchorRect ?? null);
      setIsOpen(true);
      preparePanelData();
    }

    window.addEventListener(TIMER_PANEL_OPEN_EVENT, handleTimerPanelOpen);
    return () => window.removeEventListener(TIMER_PANEL_OPEN_EVENT, handleTimerPanelOpen);
  }, [preparePanelData]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (clockRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  function handleSelectProject(id: string): void {
    setProjectId(id || null);
    setTaskId(null);
    setTasks([]);
    if (id) void loadTasks(id);
  }

  const canStart = Boolean(displayProjectId) && Boolean(displayTaskId);
  const cardKind = resolveTimerCardKind(canStart, isRunning);

  async function handleStartStop(): Promise<void> {
    if (timer.busy) return;

    if (isRunning && timer.runningTaskId && timer.runningProjectId) {
      await timer.toggle(timer.runningTaskId, timer.runningProjectId);
      return;
    }

    if (!displayProjectId || !displayTaskId) {
      show({ tone: 'warning', title: 'Selecione projeto e tarefa antes de iniciar.' });
      return;
    }
    await timer.toggle(displayTaskId, displayProjectId, {
      projectName: selectedProject?.primary,
      taskName: selectedTask?.primary,
    });
  }

  function handleToggleMiniPlayer(): void {
    timer.toggleMiniPlayerVisible();
    setIsOpen(false);
  }

  return (
    <div className="flex flex-col items-center">
      {!timer.miniPlayerVisible && (
        <button
          ref={clockRef}
          type="button"
          onClick={handleOpen}
          title="Cronômetro"
          aria-label="Abrir cronômetro"
          aria-expanded={isOpen}
          data-active={isOpen || isRunning ? 'true' : 'false'}
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-150 cursor-pointer',
            'app-sidebar-clock',
            isOpen || isRunning
              ? 'brand-fill bg-sidebar-icon-active text-white'
              : 'border border-line bg-sidebar text-white hover:bg-white/10',
          )}
        >
          <Clock size={19} />
          {isRunning && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-400 ring-2 ring-ink" />
          )}
        </button>
      )}

      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Cronômetro"
              style={{ top: panelPos.top, left: panelPos.left }}
              className={cn('fixed z-50', PANEL)}
            >
          <div className="absolute right-2 top-2 flex items-center">
            <button type="button" className={ICON_BUTTON} aria-label="Fechar cronômetro" onClick={() => setIsOpen(false)}>
              <X size={24} />
            </button>
          </div>

          <>
              <div className="flex h-6 items-center pr-20">
                <h2 className="text-xl font-bold text-ink">Cronômetro</h2>
              </div>

              <div className="mt-6 flex flex-col gap-5">
                <Field
                  label="Projeto"
                  labelClassName="text-sm font-semibold text-ink"
                  className="gap-2"
                >
                  <TimerSelectWithSearch
                    placeholder={
                      projectSearchLoading
                        ? 'Buscando projetos…'
                        : projects.length === 0
                          ? 'Nenhum projeto encontrado'
                          : 'Selecione o projeto'
                    }
                    disabled={isRunning || projectSearchLoading}
                    loading={projectSearchLoading}
                    value={selectedProject}
                    options={projects}
                    searchValue={projectSearch}
                    onSearchChange={setProjectSearch}
                    onSelect={handleSelectProject}
                  />
                </Field>

                <Field
                  label="Tarefa"
                  labelClassName="text-sm font-semibold text-ink"
                  className={cn('gap-2', !selectedProject && 'opacity-[0.48]')}
                >
                  <TimerSelect
                    placeholder={
                      !selectedProject
                        ? 'Selecione a tarefa'
                        : tasksLoading
                          ? 'Carregando tarefas…'
                          : tasks.length === 0
                            ? 'Sem tarefas neste projeto'
                            : 'Selecione a tarefa'
                    }
                    disabled={isRunning || !selectedProject || tasksLoading || taskOptions.length === 0}
                    loading={tasksLoading}
                    value={selectedTask}
                    options={taskOptions}
                    onSelect={(id) => setTaskId(id || null)}
                  />
                </Field>

                <div className="flex flex-col gap-2">
                  <div className="flex h-3 items-center gap-1">
                    <span className="text-sm font-semibold text-ink">Nota</span>
                    <span className="text-xs text-ink-muted">(Opcional)</span>
                  </div>
                  <Textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Deixe um comentário"
                    maxLength={500}
                  />
                </div>
              </div>

              <div className="mt-6">
                <TimerCard
                  kind={cardKind}
                  seconds={seconds}
                  busy={timer.busy}
                  onToggle={() => void handleStartStop()}
                  miniPlayerVisible={timer.miniPlayerVisible}
                  onToggleMiniPlayer={handleToggleMiniPlayer}
                />
              </div>
          </>
        </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function TimerCard({
  kind,
  seconds,
  busy,
  onToggle,
  miniPlayerVisible,
  onToggleMiniPlayer,
}: {
  kind: ReturnType<typeof resolveTimerCardKind>;
  seconds: number;
  busy: boolean;
  onToggle: () => void;
  miniPlayerVisible: boolean;
  onToggleMiniPlayer: () => void;
}) {
  return (
    <div
      className={cn(
        'relative flex h-10 w-full items-center gap-2 rounded-xl border border-line bg-surface px-2 py-1',
        env.enableTimerMiniPlayer && 'pr-10',
        kind === 'disabled' && 'opacity-[0.48]',
      )}
    >
      <TimerToggleButton
        running={kind === 'stop'}
        busy={busy}
        disabled={kind === 'disabled'}
        onToggle={onToggle}
        title={kind === 'stop' ? 'Parar e registrar' : 'Iniciar'}
      />
      <span
        className={cn(
          'text-base',
          kind === 'stop' ? 'text-ink' : 'text-placeholder',
        )}
      >
        {formatTimerHMS(seconds)}
      </span>
      {env.enableTimerMiniPlayer ? (
        <button
          type="button"
          onClick={onToggleMiniPlayer}
          aria-pressed={miniPlayerVisible}
          aria-label={miniPlayerVisible ? 'Ocultar mini-player do timer' : 'Mostrar mini-player do timer'}
          title={miniPlayerVisible ? 'Ocultar mini-player' : 'Mostrar mini-player'}
          className={cn(
            'absolute right-1.5 bottom-1.5 flex size-7 cursor-pointer items-center justify-center rounded-lg text-ink-muted hover:bg-hover hover:text-ink',
            miniPlayerVisible && 'bg-brand-subtle text-brand-accent',
          )}
        >
          <ListMusic size={15} />
        </button>
      ) : null}
    </div>
  );
}

function TimerSelectWithSearch({
  placeholder,
  value,
  options,
  onSelect,
  searchValue,
  onSearchChange,
  disabled,
  loading,
}: {
  placeholder: string;
  value: TimerOption | null;
  options: TimerOption[];
  onSelect: (id: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    onSearchChange('');
  }, [onSearchChange]);

  useEffect(() => {
    if (!open) return;
    function handle(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, close]);

  const menuOpen = open && !disabled;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-xl border border-line bg-surface px-2 text-left text-base',
          disabled ? 'cursor-not-allowed border-transparent bg-hover text-ink' : 'cursor-pointer',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', value ? 'text-ink' : 'text-placeholder')}>
          {value?.primary ?? placeholder}
        </span>
        {loading ? (
          <Loader2 size={16} className="shrink-0 animate-spin text-ink-muted" />
        ) : !disabled ? (
          <ChevronDown size={16} className="shrink-0 text-ink-muted" />
        ) : null}
      </button>

      {menuOpen ? (
        <div className="absolute top-full left-0 z-50 mt-1 flex w-full flex-col gap-1 rounded-xl border border-line bg-surface p-1 shadow-xl">
          <div className="relative shrink-0">
            <Search size={20} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-placeholder" />
            <input
              autoFocus
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar projeto…"
              aria-label="Buscar projeto"
              className="h-10 w-full rounded-xl border border-line bg-surface pl-9 pr-2 text-base text-ink placeholder:text-placeholder focus:border-brand-outline focus:outline-none"
            />
          </div>
          <div role="listbox" className="max-h-52 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={value?.id === option.id}
                onClick={() => {
                  onSelect(option.id);
                  close();
                }}
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left text-sm text-ink cursor-pointer',
                  value?.id === option.id ? 'bg-canvas font-semibold' : 'hover:bg-canvas',
                )}
              >
                <span className="truncate">{option.primary}</span>
                {option.secondary ? <span className="truncate text-xs font-normal text-ink-muted">{option.secondary}</span> : null}
              </button>
            ))}
            {options.length === 0 ? (
              <p className="px-2 py-3 text-sm text-ink-muted">Nenhum resultado encontrado.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TimerSelect({
  placeholder,
  value,
  options,
  onSelect,
  disabled,
  loading,
}: {
  placeholder: string;
  value: TimerOption | null;
  options: TimerOption[];
  onSelect: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  useEffect(() => {
    if (!open) return;
    function handle(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, close]);

  const menuOpen = open && !disabled;
  const visibleOptions = useMemo(() => {
    if (!search) return [...options];
    const query = search.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    return options.filter((option) =>
      (option.primary + ' ' + (option.secondary ?? '')).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().includes(query),
    );
  }, [options, search]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-xl border border-line bg-surface px-2 text-left text-base',
          disabled ? 'cursor-not-allowed border-transparent bg-hover text-ink' : 'cursor-pointer',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', value ? 'text-ink' : 'text-placeholder')}>
          {value?.primary ?? placeholder}
        </span>
        {loading ? (
          <Loader2 size={16} className="shrink-0 animate-spin text-ink-muted" />
        ) : !disabled ? (
          <ChevronDown size={16} className="shrink-0 text-ink-muted" />
        ) : null}
      </button>

      {menuOpen ? (
        <div className="absolute top-full left-0 z-50 mt-1 flex w-full flex-col gap-1 rounded-xl border border-line bg-surface p-1 shadow-xl">
          <div className="relative shrink-0">
            <Search size={20} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-placeholder" />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar"
              aria-label={`Buscar em ${placeholder.toLowerCase()}`}
              className="h-10 w-full rounded-xl border border-line bg-surface pl-9 pr-2 text-base text-ink placeholder:text-placeholder focus:border-brand-outline focus:outline-none"
            />
          </div>
          <div role="listbox" className="max-h-52 overflow-y-auto">
            {visibleOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={value?.id === option.id}
                onClick={() => {
                  onSelect(option.id);
                  close();
                }}
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left text-sm text-ink cursor-pointer',
                  value?.id === option.id ? 'bg-canvas font-semibold' : 'hover:bg-canvas',
                )}
              >
                <span className="truncate">{option.primary}</span>
                {option.secondary ? <span className="truncate text-xs font-normal text-ink-muted">{option.secondary}</span> : null}
              </button>
            ))}
            {visibleOptions.length === 0 ? (
              <p className="px-2 py-3 text-sm text-ink-muted">Nenhum resultado encontrado.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
